/** EMBER & PALM — route-specific metadata with concise, human-first local SEO copy. */
import { useEffect } from "react";

type PageMetaProps = {
  title: string;
  description: string;
  path: string;
  image?: string;
  robots?: string;
};

const origin = "https://www.angkorflavours.com.au";

function setMeta(selector: string, attribute: string, value: string) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    if (selector.includes("property=")) {
      node.setAttribute(
        "property",
        selector.match(/property="([^"]+)/)?.[1] ?? ""
      );
    } else {
      node.setAttribute("name", selector.match(/name="([^"]+)/)?.[1] ?? "");
    }
    document.head.appendChild(node);
  }
  node.setAttribute(attribute, value);
}

export function PageMeta({
  title,
  description,
  path,
  image,
  robots = "index, follow, max-image-preview:large, max-snippet:-1",
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", `${origin}${path}`);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    if (image) setMeta('meta[property="og:image"]', "content", image);

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${origin}${path}`;

    window.requestAnimationFrame(() => {
      const hash = window.location.hash.replace(/^#/, "");
      if (hash) {
        const target = document.getElementById(decodeURIComponent(hash));
        if (target) {
          if (target.id === "legend") {
            target.scrollIntoView({ block: "start", behavior: "auto" });
            return;
          }
          const focusTarget =
            target.querySelector<HTMLElement>(
              "h1, h2, input, select, textarea, button, a[href]"
            ) ?? target;
          focusTarget.tabIndex = -1;
          focusTarget.focus({ preventScroll: true });
          target.scrollIntoView({ block: "start", behavior: "auto" });
          return;
        }
      }

      window.scrollTo({ top: 0, behavior: "auto" });
      document.getElementById("main-content")?.focus({ preventScroll: true });
    });
  }, [description, image, path, robots, title]);

  return null;
}
